import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/misc';
import { ContactForm } from '@/components/ContactForm';
import { Modal } from '@/components/ui/Modal';
import { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const [contactOpen, setContactOpen] = useState(false);
  const { data: config } = useQuery({
    queryKey: ['public-config'],
    queryFn: () => api.getPublicConfig(),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-primary">Settings</h1>
        <p className="mt-1 text-sm text-text-secondary">Manage your account and plan.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Name" value={user?.displayName ?? '—'} />
            <Row label="Email" value={user?.email ?? '—'} />
            <div className="pt-2">
              <Button variant="secondary" onClick={logout}>
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Plan</CardTitle>
            <Badge tone="teal">Launch-free</Badge>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Projects" value={`up to ${config?.maxProjects ?? 3}`} />
            <Row label="Analyses / day" value={`${config?.maxRegisteredRunsPerDay ?? 5}`} />
            <Row label="Prompts / analysis" value={`${config?.maxPromptsPerRun ?? 15}`} />
            <Row label="Saved history" value={`${config?.savedHistoryDays ?? 90} days`} />
            <p className="pt-2 text-xs text-text-muted">
              Visiward is free during the launch period.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Advanced analysis — locked, contact-only */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Analysis</CardTitle>
          <Badge tone="violet">Invite only</Badge>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-secondary">
            Available for selected businesses and agencies. Contact us for access.
          </p>
          <Button className="mt-4" variant="secondary" onClick={() => setContactOpen(true)}>
            Contact us for access
          </Button>
        </CardContent>
      </Card>

      <Modal open={contactOpen} onClose={() => setContactOpen(false)} title="Request Advanced Analysis">
        <ContactForm defaultReason="Advanced Analysis" />
      </Modal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className="font-medium text-text-primary">{value}</span>
    </div>
  );
}
